import useSWR, { useSWRConfig, type SWRConfiguration } from "swr";
import { useCallback, useMemo } from "react";
import { useActiveWorkspaceScopeKey } from "@/lib/active-workspace";
import { swrFetcher } from "@/lib/swr-fetcher";
import type { ApiResponse } from "../model";
import { ApiResponseSchema } from "../schemas/system-schemas";
import {
  MCPPayloadSchema,
  MCPSchema,
  MCPUpdatePayloadSchema,
  type MCP,
  type MCPPayload,
  type MCPUpdatePayload,
} from "../schemas/mcps-schemas";
import {
  getMcpDetailUrl,
  matchesMcpsCacheKey,
  MCPS_LIST_URL,
  revalidateMcpsCache,
} from "../mcps-cache";
import {
  createMcp as createMcpRequest,
  deleteMcp as deleteMcpRequest,
  updateMcp as updateMcpRequest,
  switchMcpStatus as switchMcpStatusRequest,
} from "../mcps-service";

function useWorkspaceScopeKey() {
  return useActiveWorkspaceScopeKey();
}

// Shared generic ApiResponse wrapper, parameterized with the MCP server schema.
const McpApiResponseSchema = ApiResponseSchema(MCPSchema);

type McpListParams = {
  mcpId?: string | null;
  includeTotal?: boolean;
  search?: string;
  limit?: number;
  offset?: number;
  sort?: Record<string, 1 | -1>;
  filter?: Record<string, unknown>;
  /** When false the SWR fetch is skipped entirely (key set to null). Defaults to true. */
  enabled?: boolean;
  swr?: SWRConfiguration<ApiResponse<MCP[]>>;
};

// Single QUERY reader for the /v1/mcp entity — the one wire path both useMcps
// (list) and useMcp (detail) share. api-connector reads the filter from the
// request body as a common.Query[MCP]: omit mcpId to list all, pass it to
// narrow to a single server. (No GET; tenant scope is pinned server-side.)
function queryMcps(params?: McpListParams) {
  const body = {
    ...((params?.mcpId || params?.filter)
      ? {
          filter: {
            ...(params?.filter ?? {}),
            ...(params?.mcpId ? { mcp_id: params.mcpId } : {}),
          },
        }
      : {}),
    ...(typeof params?.limit === "number" ? { limit: params.limit } : {}),
    ...(typeof params?.offset === "number" ? { offset: params.offset } : {}),
    ...(params?.sort ? { sort_field: params.sort } : {}),
    ...(params?.search?.trim() ? { search: params.search.trim() } : {}),
    ...(!params?.mcpId && params?.includeTotal
      ? { options: { count: true } }
      : {}),
  };

  return swrFetcher(MCPS_LIST_URL, McpApiResponseSchema, {
    method: "QUERY",
    body: JSON.stringify(body),
  }).catch((error: unknown) => {
    const isListQuery = !params?.mcpId;
    const message =
      error instanceof Error ? error.message.toLowerCase() : "";

    // Temporary frontend fallback: some environments return 404 for an empty
    // MCP list. Treat that as "no rows" so SWR doesn't keep retrying a
    // non-failure empty state.
    if (isListQuery && (message.includes("not found") || message.includes("404"))) {
      return {
        status: "OK" as const,
        data: [],
        total: 0,
      };
    }

    throw error;
  });
}

// Fetch all MCP servers for the active workspace. No kind filtering — the
// backend /v1/mcp endpoint only ever returns MCP rows.
export function useMcps(params?: Omit<McpListParams, "mcpId">) {
  const workspaceScope = useWorkspaceScopeKey();
  const enabled = params?.enabled !== false;
  const paramsKey = JSON.stringify({
    limit: params?.limit ?? null,
    offset: params?.offset ?? null,
    includeTotal: params?.includeTotal ?? false,
    search: params?.search?.trim() || null,
    sort: params?.sort ?? null,
    filter: params?.filter ?? null,
  });

  const { data, error, isLoading, isValidating, mutate } = useSWR<
    ApiResponse<MCP[]>
  >(
    !enabled || workspaceScope === "pending"
      ? null
      : [MCPS_LIST_URL, workspaceScope, paramsKey],
    () => queryMcps(params),
    params?.swr,
  );

  return {
    mcps: data?.data,
    total: data?.total,
    isLoading,
    isValidating,
    error,
    mutate,
  };
}

// Fetch a single MCP server by its mcp_id.
export function useMcp(serverId: string | null) {
  const workspaceScope = useWorkspaceScopeKey();
  const { data, error, isLoading } = useSWR<ApiResponse<MCP[]>>(
    serverId && workspaceScope !== "pending"
      ? [getMcpDetailUrl(serverId), workspaceScope]
      : null,
    () => queryMcps({ mcpId: serverId }),
  );

  return {
    mcp: data?.data?.[0],
    isLoading,
    error,
  };
}

// Resolve a single MCP server, falling back to the list cache so navigating
// from the list to detail renders instantly. Mirrors useResolvedSystem.
export function useResolvedMcp(serverId: string | null) {
  const {
    mcp,
    isLoading: isServerLoading,
    error: serverError,
  } = useMcp(serverId);
  const {
    mcps,
    isLoading: areServersLoading,
    error: serversError,
  } = useMcps();

  const resolved = useMemo(() => {
    if (mcp) return mcp;
    if (!serverId) return undefined;
    return mcps?.find((candidate) => candidate.mcp_id === serverId);
  }, [mcp, serverId, mcps]);

  return {
    mcp: resolved,
    isLoading:
      Boolean(serverId) &&
      (isServerLoading || (!resolved && areServersLoading)),
    error: serverError ?? serversError,
  };
}

// Hook for creating an MCP server.
export function useCreateMcp() {
  return useCallback(
    async (payload: MCPPayload): Promise<MCP> => {
      const validatedPayload = MCPPayloadSchema.parse(payload);
      return createMcpRequest(validatedPayload);
    },
    [],
  );
}

// Hook for updating an MCP server.
export function useUpdateMcp() {
  const { mutate } = useSWRConfig();

  return useCallback(
    async (
      serverId: string,
      updates: MCPUpdatePayload,
    ): Promise<MCP> => {
      const validatedUpdates = MCPUpdatePayloadSchema.parse(updates);
      const updated = await updateMcpRequest(serverId, validatedUpdates);

      // Patch both the list and the detail cache with the fresh row.
      const patch = (current: ApiResponse<MCP[]> | undefined) => {
        if (!current?.data?.length) return current;
        let changed = false;
        const next = current.data.map((server) => {
          if (server.mcp_id !== serverId) return server;
          changed = true;
          return updated;
        });
        return changed ? { ...current, data: next } : current;
      };

      mutate<ApiResponse<MCP[]>>(
        (key) => matchesMcpsCacheKey(key, MCPS_LIST_URL),
        patch,
        { revalidate: false },
      );
      mutate<ApiResponse<MCP[]>>(
        (key) =>
          matchesMcpsCacheKey(key, getMcpDetailUrl(serverId)),
        patch,
        { revalidate: false },
      );

      return updated;
    },
    [mutate],
  );
}

// Hook for deleting an MCP server.
export function useDeleteMcp() {
  const { mutate } = useSWRConfig();

  return useCallback(
    async (serverId: string): Promise<boolean> => {
      const deleted = await deleteMcpRequest(serverId);
      if (deleted) {
        mutate((key) => matchesMcpsCacheKey(key, MCPS_LIST_URL));
      }
      return deleted;
    },
    [mutate],
  );
}

// Hook for toggling a connector's lifecycle status (active|inactive) via the
// dedicated switch-status endpoint. Patches the list + detail caches with the
// fresh row so the Switch + list badge reflect it at once, then refetches the
// lists so status filters, sort order and counts re-evaluate it.
export function useSetMcpStatus() {
  const { mutate } = useSWRConfig();

  return useCallback(
    async (
      serverId: string,
      status: "active" | "inactive",
    ): Promise<MCP> => {
      const updated = await switchMcpStatusRequest(serverId, status);
      const updatedServer: MCP = {
        ...updated,
        // Prefer the requested lifecycle value even if an intermediary returns
        // a stale or partial representation of the MCP row.
        status,
      };

      const patch = (current: ApiResponse<MCP[]> | undefined) => {
        if (!current?.data?.length) return current;
        let changed = false;
        const next = current.data.map((server) => {
          if (server.mcp_id !== serverId) return server;
          changed = true;
          return {
            ...server,
            ...updatedServer,
          };
        });
        return changed ? { ...current, data: next } : current;
      };

      await Promise.all([
        mutate<ApiResponse<MCP[]>>(
          (key) => matchesMcpsCacheKey(key, MCPS_LIST_URL),
          patch,
          { revalidate: false },
        ),
        mutate<ApiResponse<MCP[]>>(
          (key) => matchesMcpsCacheKey(key, getMcpDetailUrl(serverId)),
          patch,
          { revalidate: false },
        ),
      ]);
      // The patch shows the new lifecycle at once. SWR discards the refresh
      // switchMcpStatus started before the patch, so refetch every list now to
      // re-apply status filters, sort order and counts.
      void revalidateMcpsCache(serverId).catch(() => undefined);

      return updatedServer;
    },
    [mutate],
  );
}
